<template>
  <div>
    <navbar :links="links" />
    <!-- Header Text -->
    <v-layout align-center>
      <div>
        <h1 class="headline mt-4 mb-2">
          {{ $t('campaignCreateNew') }}
          <span
            v-if="hasUploadgroupSelected && uploadgroups.length > 1"
          >{{ $t('for') }}
            <a
              href="javascript:void(0)"
              class="msr--text clickable"
              :title="$t('campaignCreateNewUploadGroupReselectHint')"
              @click.prevent="askChangeUploadgroup()"
            >{{ selectedUploadGroup.name }}</a></span>
        </h1>
        <span class="grey--text text--darken-2 subheading">{{
          $t('campaignCreateNewHeader')
        }}</span>
      </div>
    </v-layout>
    <div
      class="grid-container-col-2 mt-4"
      style="column-gap: 48px"
    >
      <div>
        <!-- Group -->
        <v-slide-y-transition>
          <div
            v-if="
              $user &&
                $store.getters.uploadgroups.length > 1 &&
                !hasUploadgroupSelected
            "
          >
            <upload-group-select
              :selected-upload-group-id.sync="selectedUploadGroupId"
              :hint="$t('campaignCreateNewUploadGroupHint') + '.'"
            />
          </div>
        </v-slide-y-transition>
        <!-- 'Title is not unique' warning -->
        <v-slide-x-transition>
          <v-layout
            v-if="hasUploadgroupSelected && !titleIsUnique"
            align-center
            class="error--text font-weight-bold mt-3 mb-2"
          >
            <v-icon
              color="error"
              class="mr-2"
            >
              error_outline
            </v-icon>
            {{ $t('campaignCreateNewErrorTitleIsNotUnique') }}
          </v-layout>
        </v-slide-x-transition>
        <!-- Title -->
        <v-text-field
          v-if="hasUploadgroupSelected"
          v-model="title"
          :label="$t('title')"
          :counter="100"
          :rules="[rules.titleMinLength, rules.titleMaxLength]"
          clearable
          type="search"
          class="mb-3"
        />
        <!-- Start- and Stopdate -->
        <div
          v-if="hasUploadgroupSelected"
          class="my-3"
        >
          <div class="grey--text text--darken-2 caption">
            {{ $t('campaignCreateNewSetTimingHint') }}
          </div>
          <v-layout wrap>
            <!-- Startdate -->
            <div class="menu-container mr-4">
              <v-menu
                lazy
                offset-y
                min-width="290"
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  v-model="startDate"
                  :label="$t('startdate')"
                  prepend-icon="event"
                  readonly
                />
                <v-date-picker
                  v-model="startDate"
                  width="290"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :events="isEventForChosenDateRange"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                  :allowed-dates="isNotInPast"
                />
              </v-menu>
              <v-btn
                v-if="startDate"
                flat
                icon
                small
                class="ma-0"
                @click="startDate = ''"
              >
                <v-icon class="msr--text">
                  clear
                </v-icon>
              </v-btn>
            </div>
            <!-- Stopdate -->
            <div class="menu-container mr-4">
              <v-menu
                lazy
                offset-y
                min-width="290"
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  v-model="stopDate"
                  :label="$t('stopdate')"
                  prepend-icon="event"
                  readonly
                />
                <v-date-picker
                  v-model="stopDate"
                  width="290"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :events="isEventForChosenDateRange"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                  :allowed-dates="isNotInPast"
                />
              </v-menu>
              <v-btn
                v-if="stopDate"
                flat
                icon
                small
                class="ma-0"
                @click="stopDate = ''"
              >
                <v-icon class="msr--text">
                  clear
                </v-icon>
              </v-btn>
            </div>
          </v-layout>
        </div>
      </div>
      <!-- Poster Image -->
      <v-layout
        v-if="hasUploadgroupSelected"
        class="campaign-poster-box my-3"
        style="aspect-ratio: 790 / 240"
        :class="{
          border: !posterUrl,
          dragging: imageBeingDragged,
          clickable: !posterUrl,
        }"
        column
        align-center
        justify-center
        @click="!posterUrl ? onImageUploadButtonClicked() : null"
      >
        <v-layout
          v-if="posterUrl"
          column
          style="width: 100%; max-width: 790px; max-height: 240px"
        >
          <v-img
            ref="poster"
            :aspect-ratio="790 / 240"
            class="background-checker-pattern outlined--subtle clickable"
            :src="posterUrl"
            @click="onImageUploadButtonClicked()"
          />
          <a
            href="javascript:void(0)"
            class="caption grey--text text--darken-1 mt-2 ml-auto"
            @click.stop="poster = null"
          >{{ $t('clearImage') }}</a>
        </v-layout>
        <v-layout
          v-else
          id="dnd-file-area"
          column
          justify-center
          class="pa-4 full-width ignore-mouse-events-on-child-elements"
        >
          <v-icon x-large>
            insert_photo
          </v-icon>
          <div class="mt-2 body-2 grey--text text--darken-2 text-xs-center">
            {{ $t('createNewImageUpload') }}<br>
            <span class="caption grey--text">
              {{ $t('createNewImageRecommendation') }}: 790 x 240 px
            </span>
          </div>
        </v-layout>
      </v-layout>
    </div>
    <div
      v-if="hasUploadgroupSelected"
      class="grid-container-col-2 mt-4"
      style="column-gap: 48px"
    >
      <!-- Available Content -->
      <div class="mb-5">
        <h4 class="title mb-2">
          {{ $t('campaignCreateNewAvailableContent') }}
        </h4>
        <v-text-field
          v-model="searchText"
          :label="$t('search') + ' ...'"
          clearable
          :loading="loadingContent"
          type="search"
        />
        <div
          v-if="!loadingContent && stillAvailableSpots.length === 0"
          class="grey--text text--darken-2"
        >
          {{ $t('campaignCreateNewNoContentFound') }}.
        </div>
        <transition-group name="spots-transition">
          <spot
            v-for="(spot, spotIndex) in stillAvailableSpots"
            :key="'available-spot-' + spot.ident"
            :spot="spot"
            :ctype="spot.ctype"
            :show-divider="spotIndex < stillAvailableSpots.length - 1"
            :style="{opacity: loadingContent ? 0.33 : 1.0}"
            :on-click="() => addSpotToCampaign(spot)"
            :title="$t('add')"
            no-content-selection-placeholder
          >
            <div slot="actions">
              <v-btn
                class="ml-4"
                icon
                color="success"
                dark
              >
                <v-icon medium>
                  add
                </v-icon>
              </v-btn>
            </div>
          </spot>
        </transition-group>
      </div>
      <!-- Selected Content -->
      <div>
        <h4 class="title mb-3">
          {{ $t('campaignCreateNewSelectedContent') }}
          <span
            v-if="selectedSpots.length > 0"
          >({{ selectedSpots.length }})</span>
        </h4>
        <div
          v-if="selectedSpots.length === 0"
          class="grey--text text--darken-2"
        >
          {{ $t('campaignCreateNewNoContentSelectedYet') }}.
        </div>
        <transition-group name="spots-transition">
          <spot
            v-for="(spot, spotIndex) in selectedSpots"
            :key="'selected-spot-' + spot.ident"
            :spot="spot"
            :ctype="spot.ctype"
            :show-divider="spotIndex < selectedSpots.length - 1"
            :on-click="() => removeSpotFromCampaign(spot)"
            :title="$t('delete')"
            no-content-selection-placeholder
          >
            <div slot="actions">
              <v-btn
                class="ml-4"
                icon
                color="error"
                dark
              >
                <v-icon> close </v-icon>
              </v-btn>
            </div>
          </spot>
        </transition-group>
      </div>
    </div>
    <!-- Confirm Button -->
    <v-btn
      :dark="formIsValid"
      :disabled="!formIsValid"
      round
      fixed
      bottom
      right
      :class="$desktopModeStyles"
      class="msr z-index-1"
      @click="onCampaignCreationConfirmed()"
    >
      <v-icon left>
        check
      </v-icon>
      {{ $t('confirm') }}
    </v-btn>
    <!-- Image Replacement -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';

import timing from '@/mixins/timing';
import uploadGroupSelect from '@/mixins/uploadGroupSelect';

import ImageReplacement from '@/components/ImageReplacement';
import Navbar from '@/components/Navbar';
import Spot from '@/components/Spot';
import UploadGroupSelect from '@/components/UploadGroupSelect';

const defaults = {
  selectedUploadGroupId: null,
  poster: null,
  title: '',
  selectedSpots: [],
};

export default {
  components: {
    ImageReplacement,
    Navbar,
    Spot,
    UploadGroupSelect,
  },
  mixins: [timing, uploadGroupSelect],
  data() {
    return {
      links: ['campaigns', 'campaigncreate'],
      rules: {
        titleMinLength: (v) => (!!v && v.length > 0) || this.$t('tooShort'),
        titleMaxLength: (v) => (!!v && v.length <= 100) || this.$t('tooLong'),
      },

      imageEventHandlers: {},
      imageBeingDragged: false,

      // New Campaign
      selectedUploadGroupId: defaults.selectedUploadGroupId,
      poster: defaults.poster,
      title: defaults.title,
      titleIsUnique: true,
      selectedSpots: [],

      // Contents Search
      loadingContent: false,
      searchText: '',
      availableSpots: [],
    };
  },
  computed: {
    hasUploadgroupSelected() {
      return this.selectedUploadGroupId !== null;
    },
    /**
     * Detect whether the User likely has done some meaningful input.
     *
     * This is good to know e.g. to prevent accidental navigation by
     * letting the user confirm the step.
     */
    pageHasBeenModified() {
      return (
        (this.uploadgroups.length > 1 &&
          this.selectedUploadGroupId != defaults.selectedUploadGroupId) ||
        this.title != defaults.title ||
        this.poster != defaults.poster ||
        this.startDate != null ||
        this.stopDate != null ||
        this.selectedSpots.length != defaults.selectedSpots.length
      );
    },
    formIsValid() {
      return (
        this.selectedUploadGroupId !== null &&
        this.title !== null &&
        this.title.trim() !== '' &&
        this.titleIsUnique &&
        this.selectedSpots.length > 0
      );
    },
    stillAvailableSpots() {
      const spotIdents = this.selectedSpots.map((spot) => spot.ident);
      return this.availableSpots.filter((availableSpot) => {
        return !spotIdents.includes(availableSpot.ident);
      });
    },
    /**
     * Handle poster being either a URL String or a cropped image Blob.
     */
    posterUrl() {
      if (this.poster === null) {
        return null;
      }
      if (this.poster instanceof Blob) {
        return URL.createObjectURL(this.poster);
      }
      // It's a URL already.
      return this.poster;
    },
  },
  watch: {
    pageHasBeenModified: function (modified) {
      if (modified) {
        this.$store.commit(
          'SET_PRE_ROUTE_QUESTION',
          this.$t('campaignCreateNewNavigationGuard')
        );
      } else {
        this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
      }
    },
    uploadgroups: function (_, previousUploadgroups) {
      if (previousUploadgroups.length === 0) {
        this.preselectGroupIfPossible();
      }
    },
    selectedUploadGroupId: function (groupId) {
      // Ensure drag-and-drop works.
      this.$nextTick(() => {
        this.registerImageDragAndDropEvents();
      });

      // Fetch content for selected Group.
      if (groupId) {
        this.fetchAvailableSpotsForNewCampaign();
      }

      // Reset content if User changed Group.
      if (this.availableSpots) {
        this.availableSpots = [];
      }
      if (this.selectedSpots) {
        this.selectedSpots = [];
      }
    },
    title: function () {
      this.debouncedCheckCampaignTitleUniqueWithinGroup();
    },
    searchText: function () {
      this.debouncedFetchAvailableSpotsForNewCampaign();
    },
  },
  created() {
    this.preselectGroupIfPossible();
  },
  mounted() {
    this.registerImageDragAndDropEvents();
  },
  beforeDestroy() {
    this.deregisterImageDragAndDropEvents();
  },
  methods: {
    askChangeUploadgroup() {
      const confirmed = confirm(
        this.$t('campaignCreateNewUploadGroupReselectQuestion')
      );
      if (!confirmed) {
        return;
      }
      this.selectedUploadGroupId = null;
    },
    getImageDragAndDropArea() {
      return document.querySelector('#dnd-file-area');
    },
    deregisterImageDragAndDropEvents() {
      if (!this.imageEventHandlers) {
        return;
      }

      const area = this.getImageDragAndDropArea();
      if (!area) {
        return;
      }

      area.removeEventListener('dragenter', this.imageEventHandlers.dragenter);
      area.removeEventListener('dragleave', this.imageEventHandlers.dragleave);
      area.removeEventListener('dragover', this.imageEventHandlers.dragover);
      area.removeEventListener('drop', this.imageEventHandlers.drop);

      this.imageEventHandlers = {};
    },
    registerImageDragAndDropEvents() {
      const area = this.getImageDragAndDropArea();
      if (!area) {
        return;
      }

      this.deregisterImageDragAndDropEvents();

      const vm = this;

      function onDragEnter(e) {
        vm.imageBeingDragged = true;
      }

      function onDragLeave(e) {
        e.preventDefault();
        vm.imageBeingDragged = false;
      }

      function onDragOver(e) {
        e.preventDefault();
      }

      function onDrop(e) {
        e.preventDefault();
        vm.imageBeingDragged = false;

        const files = e.dataTransfer.files;
        vm.onImageUploadButtonClicked(files[0]);
      }

      this.imageEventHandlers = {};
      this.imageEventHandlers.dragenter = onDragEnter;
      this.imageEventHandlers.dragleave = onDragLeave;
      this.imageEventHandlers.dragover = onDragOver;
      this.imageEventHandlers.drop = onDrop;

      area.addEventListener('dragenter', this.imageEventHandlers.dragenter);
      area.addEventListener('dragleave', this.imageEventHandlers.dragleave);
      area.addEventListener('dragover', this.imageEventHandlers.dragover);
      area.addEventListener('drop', this.imageEventHandlers.drop);

      console.log('[DEBUG] DnD Events registered');
    },
    onCampaignCreationConfirmed() {
      const opts = {
        title: this.title,
        contentIdents: this.selectedSpots.map((spot) => spot.ident),
      };
      if (this.poster) {
        opts.poster = this.poster;
      }
      if (this.startDate) {
        opts.defaultStartDate = this.startDate;
      }
      if (this.stopDate) {
        opts.defaultStopDate = this.stopDate;
      }

      this.$api
        .createNewCampaign(this.selectedUploadGroupId, opts)
        .then((response) => {
          const campaignId = response.data;
          const message = this.$t('campaignCreateNewSuccess', {
            title: this.title,
          });
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 3000,
          });

          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
          this.$router.push({
            name: 'campaign',
            params: {campaignId: campaignId},
          });
        });
    },
    preselectGroupIfPossible() {
      const alreadySelected = this.selectedUploadGroupId !== null;
      if (alreadySelected) {
        return;
      }
      this.selectedUploadGroupId = this.getInitialUploadgroupId();
    },
    addSpotToCampaign(spot) {
      this.selectedSpots.push(spot);
      this.debouncedFetchAvailableSpotsForNewCampaign({spinner: false});
    },
    removeSpotFromCampaign(spot) {
      for (let i = 0; i < this.selectedSpots.length; i++) {
        const selectedSpot = this.selectedSpots[i];
        if (selectedSpot.ident === spot.ident) {
          this.selectedSpots.splice(i, 1);
          this.debouncedFetchAvailableSpotsForNewCampaign({spinner: false});
          return;
        }
      }
    },
    fetchAvailableSpotsForNewCampaign({spinner = true} = {}) {
      if (spinner) {
        this.loadingContent = true;
      }

      const excludedContentIdents = this.selectedSpots.map(
        (spot) => spot.ident
      );

      return this.$api
        .listContentForNewCampaign(this.selectedUploadGroupId, {
          search: this.searchText,
          excludedContentIdents: excludedContentIdents,
        })
        .then((response) => {
          this.availableSpots = response.data;
        })
        .finally(() => (this.loadingContent = false));
    },
    debouncedFetchAvailableSpotsForNewCampaign: debounce(function () {
      return this.fetchAvailableSpotsForNewCampaign(...arguments);
    }, 500),
    checkCampaignTitleUniqueWithinGroup() {
      if (
        !this.selectedUploadGroupId ||
        !this.title ||
        this.title.trim() == ''
      ) {
        this.titleIsUnique = true;
        return;
      }
      this.$api
        .checkCampaignTitleUniqueWithinGroup(
          this.selectedUploadGroupId,
          this.title
        )
        .then((response) => {
          this.titleIsUnique = response.data;
        });
    },
    debouncedCheckCampaignTitleUniqueWithinGroup: debounce(function () {
      return this.checkCampaignTitleUniqueWithinGroup();
    }, 500),
    onImageUploadButtonClicked(droppedFile = null) {
      const opts = {
        field: {
          name: 'poster',
          data: {
            width: 790,
            height: 240,
          },
          value: null,
        },
      };
      if (droppedFile) {
        opts.recrop = true;
        opts.value = URL.createObjectURL(droppedFile);
      }
      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onImageChangeConfirmed(payload) {
      this.poster = payload.file;
    },
  },
};
</script>

<style scoped>
.menu-container {
  display: flex;
  align-items: baseline;
}

/* https://codepen.io/lloeki/pen/Dvkqw */
.background-checker-pattern {
  background-repeat: repeat;
  background-size: 16px 16px;
  background-position: 0 0, 8px 8px;
  background-image: linear-gradient(
      45deg,
      #d6d6d6 25%,
      transparent 25%,
      transparent 75%,
      #d6d6d6 75%,
      #d6d6d6
    ),
    linear-gradient(
      45deg,
      #d6d6d6 25%,
      transparent 25%,
      transparent 75%,
      #d6d6d6 75%,
      #d6d6d6
    );
}

.campaign-poster-box {
  border: 3px solid transparent; /* To keep the box-sizing. */
}
.campaign-poster-box.border {
  border: 3px dashed rgba(0, 0, 0, 0.16);
  border-radius: 8px;
}
.campaign-poster-box.border:hover,
.campaign-poster-box.border.dragging {
  border: 3px dashed rgba(0, 0, 0, 0.32);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
}

/**
 * drag and dragend events would otherwise be triggered for each,
 * causing problems with the drag-and-drop preview and/or flickering.
 */
.ignore-mouse-events-on-child-elements * {
  pointer-events: none;
}

.spots-transition-enter-active,
.spots-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.spots-transition-enter,
.spots-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/pages/CampaignCreate.vue